// ============================================================================
// ChatPinnedBlock — вкладка «Закреплённые» правой панели «О чате» (D6).
// SSoT — сервер: listPinnedMessages (новые первыми, пагинация offset);
// «Открепить» — pinMessage(pinned=false). Живое обновление — событие шины
// chat.message.pinned.{convId} (без поллинга). Клик по элементу — переход
// к сообщению в ленте (прокрутка; если сообщение вне загруженного фрагмента —
// явное сообщение пользователю, см. ChatThread.jumpToMessage).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Pin, PinOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { eventBusHub } from '@/lib/eventBusHub';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { useToastStore } from '@/store/toastStore';
import { listPinnedMessages, pinMessage } from '@/lib/chatBridge';
import { stripAssistantSources } from '@/lib/assistantSources';
import { formatStamp } from './chatInfoData';
import type { ThreadUser } from './ChatThread';

const PAGE_SIZE = 30;

interface PinnedItem {
  id: string;
  senderId: string;
  text: string;
  createdAt: string;
  isDeleted: boolean;
  hasAttachments: boolean;
}

interface ChatPinnedBlockProps {
  conversationId: string;
  myId: string;
  users: ThreadUser[];
  /** Переход к сообщению в ленте (прокрутка). */
  onJump: (messageId: string) => void;
}

function mapItem(m: any): PinnedItem {
  return {
    id: String(m?.id || ''),
    senderId: String(m?.senderId || ''),
    text: stripAssistantSources(String(m?.text || '')),
    createdAt: String(m?.createdAt || ''),
    isDeleted: !!m?.isDeleted,
    hasAttachments: Array.isArray(m?.attachments) && m.attachments.length > 0,
  };
}

export function ChatPinnedBlock({ conversationId, myId, users, onJump }: ChatPinnedBlockProps) {
  const [items, setItems] = useState<PinnedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [moreLoading, setMoreLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState('');

  const loadFirst = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const page = (await listPinnedMessages(conversationId, PAGE_SIZE, 0)).map(mapItem);
      setItems(page.filter((x) => x.id));
      setHasMore(page.length === PAGE_SIZE);
      setError(null);
    } catch (e) {
      setError(friendlyError(e));
      logErrorToJournal(e, 'Чат: закреплённые сообщения');
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    void loadFirst(true);
  }, [loadFirst]);

  // Живое обновление: закрепление/снятие в ЭТОМ чате (серверное событие D6).
  useEffect(() => {
    const topic = `chat.message.pinned.${conversationId}`;
    const un = eventBusHub.subscribe((ev) => {
      if (ev.topic !== topic) return;
      void loadFirst();
    });
    return un;
  }, [conversationId, loadFirst]);

  const loadMore = async () => {
    setMoreLoading(true);
    try {
      const page = (await listPinnedMessages(conversationId, PAGE_SIZE, items.length)).map(mapItem);
      if (page.length === 0) {
        setHasMore(false);
      } else {
        setItems((prev) => {
          const seen = new Set(prev.map((x) => x.id));
          return [...prev, ...page.filter((x) => x.id && !seen.has(x.id))];
        });
        setHasMore(page.length === PAGE_SIZE);
      }
      setError(null);
    } catch (e) {
      setError(friendlyError(e));
      logErrorToJournal(e, 'Чат: закреплённые сообщения (ещё)');
    } finally {
      setMoreLoading(false);
    }
  };

  const unpin = async (id: string) => {
    setBusyId(id);
    try {
      await pinMessage(conversationId, id, false);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      useToastStore.getState().showError(friendlyError(e));
      logErrorToJournal(e, 'Чат: открепить сообщение');
    } finally {
      setBusyId('');
    }
  };

  const nameOf = (id: string) => (
    id === myId ? 'Вы' : (users.find((u) => u.id === id)?.name || id.slice(0, 8))
  );

  if (loading) {
    return (
      <div className="glass-panel rounded-xl p-3 space-y-2 animate-pulse">
        <div className="h-3 w-28 rounded bg-muted" />
        <div className="h-4 w-full rounded bg-muted" />
        <div className="h-3 w-2/3 rounded bg-muted" />
      </div>
    );
  }

  if (error && items.length === 0) {
    return (
      <div className="glass-panel rounded-xl p-3 space-y-2">
        <div className="text-[12px] text-destructive">{error}</div>
        <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => void loadFirst(true)}>
          Повторить
        </Button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="glass-panel rounded-xl p-4 text-center space-y-1.5">
        <PinOff className="mx-auto h-5 w-5 text-muted-foreground" />
        <div className="text-[12px] font-medium">Закреплённых сообщений нет</div>
        <div className="text-[11px] text-muted-foreground">
          Наведите на сообщение и нажмите «Закрепить» — оно появится здесь у всех участников.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {items.map((it) => (
        <div key={it.id} className="glass-panel rounded-xl p-2.5 space-y-1 min-w-0">
          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground min-w-0">
            <Pin className="h-3 w-3 shrink-0 text-primary" />
            <span className="truncate font-medium text-foreground/80" title={nameOf(it.senderId)}>
              {nameOf(it.senderId)}
            </span>
            <span className="shrink-0">·</span>
            <span className="shrink-0">{formatStamp(it.createdAt)}</span>
            <button
              type="button"
              className="ml-auto shrink-0 p-1 rounded-full hover:bg-muted/50 text-muted-foreground disabled:opacity-40"
              title="Открепить"
              aria-label="Открепить сообщение"
              disabled={busyId === it.id}
              onClick={() => void unpin(it.id)}
            >
              <PinOff className="h-3 w-3" />
            </button>
          </div>
          <button
            type="button"
            className="w-full text-left min-w-0"
            title="Перейти к сообщению"
            onClick={() => onJump(it.id)}
          >
            <span className="block text-[12px] leading-snug whitespace-pre-wrap break-words line-clamp-3">
              {it.isDeleted ? 'Сообщение удалено' : (it.text || (it.hasAttachments ? '📎 вложение' : ''))}
            </span>
          </button>
        </div>
      ))}

      {error && <div className="text-[11px] text-destructive px-1">{error}</div>}

      {hasMore && (
        <Button
          size="sm"
          variant="outline"
          className="w-full h-7 text-[11px]"
          disabled={moreLoading}
          onClick={() => void loadMore()}
        >
          {moreLoading ? 'Загружаем…' : 'Показать ещё'}
        </Button>
      )}
    </div>
  );
}
